// Rotating announcement bar: one message at a time, crossfaded, with manual arrows.
// Auto-rotation pauses on hover/focus and never starts when reduced motion is requested.
class SAnnouncement extends HTMLElement {
  #controller;
  #slides = [];
  #index = 0;
  #timer;

  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-announcement-slide]')];
    if (this.#slides.length < 2) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#index = Math.max(0, this.#slides.findIndex((slide) => slide.hasAttribute('data-active')));

    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--next' && event.command !== '--previous') return;
        // Manual changes are announced; automatic ones stay silent.
        this.querySelector('[data-announcement-track]')?.setAttribute('aria-live', 'polite');
        this.#show(this.#index + (event.command === '--next' ? 1 : -1));
        this.#restart();
      },
      { signal }
    );

    for (const type of ['pointerenter', 'focusin']) this.addEventListener(type, () => this.#stop(), { signal });
    for (const type of ['pointerleave', 'focusout']) this.addEventListener(type, () => this.#restart(), { signal });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : this.#restart()), { signal });

    this.#restart();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#stop();
  }

  #show(next) {
    const count = this.#slides.length;
    this.#index = (next + count) % count;
    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
    });
  }

  #restart() {
    this.#stop();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;
    const interval = Number(this.dataset.interval) || 4500;
    this.#timer = window.setInterval(() => this.#show(this.#index + 1), interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
  }
}

customElements.define('s-announcement', SAnnouncement);
